import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Check, X, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BatchRowData, ValidationError } from '@/hooks/useBatchJobs';

interface BatchPreviewStepProps {
  data: BatchRowData[];
  errors: ValidationError[];
  columns: string[];
  onDataChange: (data: BatchRowData[]) => void;
  onReupload: () => void;
}

export function BatchPreviewStep({
  data,
  errors,
  columns,
  onDataChange,
  onReupload,
}: BatchPreviewStepProps) {
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  const getRowErrors = (rowIndex: number) => {
    return errors.filter(e => e.row === rowIndex);
  };

  const getCellError = (rowIndex: number, field: string) => {
    return errors.find(e => e.row === rowIndex && e.field === field);
  };

  const handleStartEdit = (rowIndex: number, field: string, value: string) => {
    setEditingCell({ row: rowIndex, field });
    setEditValue(value || '');
  };

  const handleSaveEdit = () => {
    if (!editingCell) return;
    
    const newData = [...data];
    newData[editingCell.row] = {
      ...newData[editingCell.row],
      [editingCell.field]: editValue,
    };
    onDataChange(newData);
    setEditingCell(null);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newData = data.filter((_, i) => i !== rowIndex);
    onDataChange(newData);
  };

  const handleAddRow = () => {
    const newRow: BatchRowData = { name: '', type: '' };
    columns.forEach(col => {
      if (!newRow[col]) newRow[col] = '';
    });
    onDataChange([...data, newRow]);
  };

  const errorCount = errors.length;
  const validCount = data.filter((_, i) => getRowErrors(i).length === 0).length;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {/* Summary Bar */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-sm">
            {data.length} rows
          </Badge>
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-primary" />
            <span>{validCount} valid</span>
          </div>
          {errorCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{errorCount} errors</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onReupload}>
            Re-upload CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleAddRow}>
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <div className="surface-elevated rounded-xl overflow-hidden">
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead className="w-16">Status</TableHead>
                {columns.map(col => (
                  <TableHead key={col} className="min-w-[120px]">
                    {col}
                  </TableHead>
                ))}
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, rowIndex) => {
                const rowErrors = getRowErrors(rowIndex);
                const hasErrors = rowErrors.length > 0;
                
                return (
                  <TableRow
                    key={rowIndex}
                    className={hasErrors ? 'bg-destructive/5' : undefined}
                  >
                    <TableCell className="text-muted-foreground">
                      {rowIndex + 1}
                    </TableCell>
                    <TableCell>
                      {hasErrors ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="destructive" className="text-xs">
                              <X className="h-3 w-3 mr-1" />
                              {rowErrors.length}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <ul className="text-xs space-y-1">
                              {rowErrors.map((err, i) => (
                                <li key={i}>
                                  {err.field}: {err.message}
                                </li>
                              ))}
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Badge variant="outline" className="text-xs text-primary border-primary/30">
                          <Check className="h-3 w-3" />
                        </Badge>
                      )}
                    </TableCell>
                    
                    {columns.map(col => {
                      const cellError = getCellError(rowIndex, col);
                      const isEditing = editingCell?.row === rowIndex && editingCell?.field === col;
                      
                      return (
                        <TableCell
                          key={col}
                          className={cellError ? 'bg-destructive/10' : undefined}
                        >
                          {isEditing ? (
                            <div className="flex gap-1">
                              <Input
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                className="h-8 text-sm"
                                autoFocus
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={handleSaveEdit}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded text-sm truncate max-w-[200px]"
                                  onClick={() => handleStartEdit(rowIndex, col, row[col])}
                                >
                                  {row[col] || <span className="text-muted-foreground italic">empty</span>}
                                </div>
                              </TooltipTrigger>
                              {cellError && (
                                <TooltipContent className="text-destructive">
                                  {cellError.message}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          )}
                        </TableCell>
                      );
                    })}
                    
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleStartEdit(rowIndex, columns[0], row[columns[0]])}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteRow(rowIndex)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </motion.div>
  );
}
